import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { buildWbsTableTree } from '@/utils/est3WbsTree';
import {
  layerLabel,
  type Est3WbsLayer,
  WBS_LAYER_HINTS,
} from '@/utils/est3WbsLayers';
import { EpcEstimateWbsTreeSelect } from './EpcEstimateWbsTreeSelect';

const { Text, Paragraph } = Typography;

const STRUCTURE_LAYER_CODES = ['PROJECT', 'UNIT', 'AREA', 'BLOCK'] as const;
const DETAIL_LAYER_CODES = ['MODULE', 'PACKAGE'] as const;

export interface GuidedWbsDraftRow {
  key: string;
  code: string;
  name: string;
  wbsLayerCode: string;
  parentCode?: string;
  description?: string;
  shortName?: string;
  referenceScopeCode?: string;
  sortOrder?: number;
}

export interface EpcEstimateWbsGuidedSetupWizardProps {
  open: boolean;
  onClose: () => void;
  estimateId: string;
  revisionId: string;
  readOnly?: boolean;
  existingNodes?: Array<{
    id: string;
    code: string;
    name?: string;
    parent_id?: string | null;
    wbs_layer_id?: string | null;
    layer_code?: string;
  }>;
  onCompleted: () => void;
}

function filterDraftsByLayers(drafts: GuidedWbsDraftRow[], codes: readonly string[]) {
  const set = new Set(codes);
  return drafts.filter((d) => set.has(d.wbsLayerCode));
}

export const EpcEstimateWbsGuidedSetupWizard: React.FC<EpcEstimateWbsGuidedSetupWizardProps> = ({
  open,
  onClose,
  estimateId,
  revisionId,
  readOnly,
  existingNodes = [],
  onCompleted,
}) => {
  const [step, setStep] = useState(0);
  const [drafts, setDrafts] = useState<GuidedWbsDraftRow[]>([]);
  const [alignChecks, setAlignChecks] = useState({
    engineering: false,
    construction: false,
    controls: false,
    commissioning: false,
  });
  const [form] = Form.useForm();

  const layersQ = useQuery({
    queryKey: ['epc-wbs-layers', estimateId],
    queryFn: () => epcEstimationService.listWbsLayers(estimateId),
    enabled: open,
  });

  const layers = (layersQ.data || []) as Est3WbsLayer[];
  const layerByCode = useMemo(() => new Map(layers.map((l) => [l.code, l])), [layers]);

  const allowedLayerCodes = useMemo(() => {
    if (step === 1) return STRUCTURE_LAYER_CODES as unknown as string[];
    if (step === 3) return DETAIL_LAYER_CODES as unknown as string[];
    return layers.map((l) => l.code);
  }, [step, layers]);

  const selectedLayerCode = Form.useWatch('wbsLayerCode', form);

  const parentOptions = useMemo(() => {
    const layer = selectedLayerCode ? layerByCode.get(selectedLayerCode) : undefined;
    if (!layer) return [];
    const parentDepth = Number(layer.depth_order) - 1;
    if (parentDepth < 0) return [];
    const parentLayer = layers.find((l) => Number(l.depth_order) === parentDepth);
    if (!parentLayer) return [];

    const fromExisting = existingNodes.filter((n) => String(n.layer_code || '') === parentLayer.code);
    const fromDrafts = drafts.filter((d) => d.wbsLayerCode === parentLayer.code);
    return [
      ...fromExisting.map((n) => ({ code: n.code, name: n.name || n.code, source: 'existing' as const })),
      ...fromDrafts.map((d) => ({ code: d.code, name: d.name, source: 'draft' as const })),
    ];
  }, [existingNodes, selectedLayerCode, layerByCode, layers, drafts]);

  const structureDrafts = useMemo(
    () => filterDraftsByLayers(drafts, STRUCTURE_LAYER_CODES),
    [drafts]
  );
  const detailDrafts = useMemo(() => filterDraftsByLayers(drafts, DETAIL_LAYER_CODES), [drafts]);

  const previewNodes = useMemo(() => {
    const codeToKey = new Map<string, string>();
    for (const n of existingNodes) codeToKey.set(n.code, n.id);
    for (const d of drafts) codeToKey.set(d.code, d.key);

    const merged = [
      ...existingNodes.map((n) => ({
        id: n.id,
        code: n.code,
        name: n.name || n.code,
        parent_id: n.parent_id,
        layer_code: n.layer_code,
      })),
      ...drafts.map((d) => ({
        id: d.key,
        code: d.code,
        name: d.name,
        parent_id: d.parentCode ? codeToKey.get(d.parentCode) || null : null,
        layer_code: d.wbsLayerCode,
      })),
    ];
    return buildWbsTableTree(merged as any);
  }, [drafts, existingNodes]);

  const structurePreview = useMemo(() => {
    const structureCodes = new Set(STRUCTURE_LAYER_CODES);
    const filterTree = (nodes: any[]): any[] =>
      nodes
        .map((n) => ({
          ...n,
          children: n.children ? filterTree(n.children) : undefined,
        }))
        .filter((n) => structureCodes.has(n.layer_code) || (n.children && n.children.length));
    return filterTree(previewNodes);
  }, [previewNodes]);

  const setupM = useMutation({
    mutationFn: () =>
      epcEstimationService.guidedSetupWbs(estimateId, {
        revisionId,
        nodes: drafts.map((d) => ({
          code: d.code,
          name: d.name,
          wbsLayerCode: d.wbsLayerCode,
          parentCode: d.parentCode,
          description: d.description,
          shortName: d.shortName,
          referenceScopeCode: d.referenceScopeCode,
          sortOrder: d.sortOrder,
        })),
      }),
    onSuccess: (data) => {
      message.success(`Created ${data?.created ?? 0} WBS node(s)`);
      onCompleted();
      handleClose();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Setup failed'),
  });

  const reset = () => {
    setStep(0);
    setDrafts([]);
    setAlignChecks({ engineering: false, construction: false, controls: false, commissioning: false });
    form.resetFields();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const addDraft = async () => {
    const v = await form.validateFields();
    if (!allowedLayerCodes.includes(v.wbsLayerCode)) {
      message.error('Layer not allowed in this step');
      return;
    }
    const parentNode = v.parentCode
      ? [...existingNodes, ...drafts].find((n) => n.code === v.parentCode)
      : undefined;
    setDrafts((prev) => [
      ...prev,
      {
        key: `draft-${Date.now()}-${prev.length}`,
        code: v.code,
        name: v.name,
        wbsLayerCode: v.wbsLayerCode,
        parentCode: parentNode ? v.parentCode : v.parentCode || undefined,
        description: v.description,
        shortName: v.shortName,
        referenceScopeCode: v.referenceScopeCode,
        sortOrder: v.sortOrder,
      },
    ]);
    form.setFieldsValue({
      code: undefined,
      name: undefined,
      description: undefined,
      shortName: undefined,
      referenceScopeCode: undefined,
      parentCode: v.parentCode,
    });
  };

  const draftTable = (rows: GuidedWbsDraftRow[]) => (
    <Table
      size="small"
      rowKey="key"
      dataSource={rows}
      pagination={false}
      columns={[
        { title: 'Layer', dataIndex: 'wbsLayerCode', width: 100 },
        { title: 'Code', dataIndex: 'code', width: 100 },
        { title: 'Name', dataIndex: 'name', ellipsis: true },
        { title: 'Parent', dataIndex: 'parentCode', width: 100 },
        {
          title: '',
          width: 80,
          render: (_: unknown, r: GuidedWbsDraftRow) =>
            !readOnly ? (
              <Button
                size="small"
                danger
                type="link"
                onClick={() => setDrafts((prev) => prev.filter((x) => x.key !== r.key))}
              >
                Remove
              </Button>
            ) : null,
        },
      ]}
    />
  );

  const nodeForm = (
    <Form form={form} layout="vertical">
      <Space wrap align="start" style={{ width: '100%' }}>
        <Form.Item
          name="wbsLayerCode"
          label="Layer"
          rules={[{ required: true }]}
          style={{ minWidth: 160 }}
        >
          <Select
            placeholder="Select layer"
            options={layers
              .filter((l) => allowedLayerCodes.includes(l.code))
              .map((l) => ({ value: l.code, label: layerLabel(l) }))}
            onChange={() => form.setFieldValue('parentCode', undefined)}
          />
        </Form.Item>
        <Form.Item name="parentCode" label="Parent" style={{ minWidth: 200 }}>
          <Select
            allowClear
            showSearch
            placeholder="Parent node"
            disabled={
              !selectedLayerCode || Number(layerByCode.get(selectedLayerCode)?.depth_order) === 0
            }
            options={parentOptions.map((n) => ({
              value: n.code,
              label: `${n.code} · ${n.name}`,
            }))}
          />
        </Form.Item>
        <Form.Item name="code" label="Code" rules={[{ required: true }]} style={{ minWidth: 120 }}>
          <Input placeholder="e.g. U01" />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true }]} style={{ minWidth: 180 }}>
          <Input placeholder="Node name" />
        </Form.Item>
        <Form.Item name="sortOrder" label="Sort" style={{ width: 90 }}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      </Space>
      <Space wrap>
        <Form.Item name="description" label="Description" style={{ minWidth: 240 }}>
          <Input placeholder="Optional scope description" />
        </Form.Item>
        <Form.Item name="shortName" label="Short name" style={{ minWidth: 140 }}>
          <Input placeholder="Alias" />
        </Form.Item>
        <Form.Item name="referenceScopeCode" label="Reference scope" style={{ minWidth: 160 }}>
          <Input placeholder="Benchmark code" />
        </Form.Item>
      </Space>
      <Button type="dashed" icon={<PlusOutlined />} onClick={() => void addDraft()}>
        Add to draft
      </Button>
    </Form>
  );

  const alignReady =
    alignChecks.engineering && alignChecks.construction && alignChecks.controls && alignChecks.commissioning;

  return (
    <Modal
      title="Guided WBS setup (EPC)"
      open={open}
      onCancel={handleClose}
      width={920}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          {step > 0 && step < 4 && (
            <Button onClick={() => setStep((s) => s - 1)}>Back</Button>
          )}
          {step === 0 && (
            <Button type="primary" onClick={() => setStep(1)}>
              Start structure (L0–L3)
            </Button>
          )}
          {step === 1 && (
            <Button type="primary" onClick={() => setStep(2)}>
              Review & align
            </Button>
          )}
          {step === 2 && (
            <Button type="primary" disabled={!alignReady} onClick={() => setStep(3)}>
              Continue to detail (L4–L5)
            </Button>
          )}
          {step === 3 && (
            <Button type="primary" disabled={!drafts.length} onClick={() => setStep(4)}>
              Preview {drafts.length} node(s)
            </Button>
          )}
          {step === 4 && (
            <Button
              type="primary"
              loading={setupM.isPending}
              disabled={readOnly || !drafts.length}
              onClick={() => setupM.mutate()}
            >
              Create WBS structure
            </Button>
          )}
        </Space>
      }
    >
      <Steps
        size="small"
        current={step}
        style={{ marginBottom: 16 }}
        items={[
          { title: 'Layer model' },
          { title: 'Structure (L0–L3)' },
          { title: 'Review & align' },
          { title: 'Detail (L4–L5)' },
          { title: 'Preview & create' },
        ]}
      />

      {step === 0 && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            EPC WBS follows a geographic/product hierarchy (Buro Matei model): Project → Unit →
            Area → Block → Module → Package. Define structure levels 0–3 first, align stakeholders,
            then add modules and packages.
          </Paragraph>
          <Table
            size="small"
            rowKey="id"
            loading={layersQ.isLoading}
            pagination={false}
            dataSource={layers}
            columns={[
              { title: 'Level', dataIndex: 'depth_order', width: 70 },
              { title: 'Code', dataIndex: 'code', width: 100 },
              { title: 'Name', dataIndex: 'name', width: 120 },
              {
                title: 'Purpose',
                render: (_: unknown, r: Est3WbsLayer) => (
                  <Text type="secondary">{WBS_LAYER_HINTS[r.code] || r.description}</Text>
                ),
              },
            ]}
          />
        </Space>
      )}

      {step === 1 && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="Define Project, Unit, Area, and Block nodes before detailing modules and packages."
          />
          {!readOnly ? nodeForm : (
            <Alert type="warning" showIcon message="Approved revision — guided setup is read-only." />
          )}
          {draftTable(structureDrafts)}
        </Space>
      )}

      {step === 2 && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Paragraph type="secondary">
            Confirm the geographic breakdown (levels 0–3) with key stakeholders before adding
            modules and work packages.
          </Paragraph>
          <Table
            size="small"
            rowKey="id"
            dataSource={structurePreview}
            pagination={false}
            expandable={{ defaultExpandAllRows: true }}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 120 },
              { title: 'Name', dataIndex: 'name', ellipsis: true },
              { title: 'Layer', dataIndex: 'layer_code', width: 100 },
            ]}
          />
          <Space direction="vertical">
            <Checkbox
              checked={alignChecks.engineering}
              onChange={(e) => setAlignChecks((c) => ({ ...c, engineering: e.target.checked }))}
            >
              Engineering reviewed Units / Areas / Blocks
            </Checkbox>
            <Checkbox
              checked={alignChecks.construction}
              onChange={(e) => setAlignChecks((c) => ({ ...c, construction: e.target.checked }))}
            >
              Construction reviewed Construction Work Areas (path of construction)
            </Checkbox>
            <Checkbox
              checked={alignChecks.controls}
              onChange={(e) => setAlignChecks((c) => ({ ...c, controls: e.target.checked }))}
            >
              Project controls confirmed coding scheme
            </Checkbox>
            <Checkbox
              checked={alignChecks.commissioning}
              onChange={(e) => setAlignChecks((c) => ({ ...c, commissioning: e.target.checked }))}
            >
              Commissioning reviewed area boundaries
            </Checkbox>
          </Space>
        </Space>
      )}

      {step === 3 && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="Add Module and Package (CWP) nodes under Block parents. Cost lines attach at Package level."
          />
          {!readOnly ? nodeForm : null}
          {draftTable(detailDrafts)}
          {structureDrafts.length > 0 && (
            <Text type="secondary">{structureDrafts.length} structure node(s) from prior step</Text>
          )}
        </Space>
      )}

      {step === 4 && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space>
            <Tag>{drafts.length} new node(s)</Tag>
            <Tag color="blue">{structureDrafts.length} structure</Tag>
            <Tag color="purple">{detailDrafts.length} detail</Tag>
            <Tag>{existingNodes.length} existing</Tag>
          </Space>
          <Table
            size="small"
            rowKey="id"
            dataSource={previewNodes}
            pagination={false}
            expandable={{ defaultExpandAllRows: true }}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 120 },
              { title: 'Name', dataIndex: 'name', ellipsis: true },
              {
                title: 'Layer',
                dataIndex: 'layer_code',
                width: 100,
                render: (v) => v || '—',
              },
            ]}
          />
        </Space>
      )}
    </Modal>
  );
};
